<template>
  <div class="profile-experience">
    <div class="profile-experience__xp-achievements">
      <div class="xp-achievements__achievements">
        <div
          v-for="item in activeAchievements"
          :key="item.achievementId"
          class="achievements__item"
          @click.stop="onSelected(item.achievementKey)"
        >
          <img :src="item.achievementIconUrl" :alt="getAchievementName(item.achievementKey)" />
        </div>
        <div
          v-if="countMore > 0"
          class="achievements__item"
          @click.stop="onSelected('Welcome')"
        >
          <img :src="imageMore" />
          <div class="item__more">+{{ countMore }}</div>
        </div>
      </div>
    </div>
    <div class="profile-experience__item">
      <div class="profile-experience__steps">
        <div class="profile-experience__steps-top">
          <Icon name="steps" size="xl"></Icon>
          <span>{{ formattedSteps  }}</span>
        </div>
        <p>{{ stepsWord }}</p>
      </div>
    </div>
    <div v-if="showIndexOFP" class="profile-experience__item">
      <IndexOFP
        v-if="sportIndexes"
        :value="sportIndexes.index"
        :dates="formatDateRange({ start: sportIndexes.startDate, finish: sportIndexes.finishDate })"
        @show="onShowIndexOFP"
      />
    </div>

    <Modal
      :active="achievementsModal"
      name="AchievementsModal"
      title="Мои достижения"
      :subtitle="`Получено ${ achievements ? achievements.filter(item => item.unlocked).length : 0 } / ${ achievements ? achievements.length : 0 }`"
      :class="{ 'for-mobile-app': isMobileApp }"
      @close="achievementsModal = false"
    >
      <div class="modal-content text-left">
        <div
          v-for="cat in categories"
          :key="cat.id"
          class="modal-content__group"
          :class="{ 'd-none': getCountOfCat(cat.id) === 0 }"
        >
          <div class="modal-content__group-title" @click.stop="!this.$root.isDesktop ? setCollapsed(cat.id) : null">
            <div class="group-title__name">
              <div class="group-title__text" v-html="cat.name || ''"></div>
              <div class="group-title__count">Получено {{ getActiveOfCatCount(cat.id) }} / {{ getCountOfCat(cat.id) }}</div>
            </div>
            <div class="group-title__icon" @click.stop="setCollapsed(cat.id)">
              <Icon v-if="cat.collapsed" name="down" size="xl" />
              <Icon v-else name="up" size="xl" />
            </div>
          </div>
          <div v-if="!cat.collapsed" class="modal-content__group-details">
            <div
              v-for="item in getAchievementsOfCat(cat.id)"
              :key="item.achievementId"
              class="group-details__achievement text-center"
            >
              <div
                class="group-details__image"
                @click.stop="onAchievementDetails(item)"
              >
                <img :src="item.achievementIconUrl" :alt="item.achievementKey || ''" />
              </div>
              <div class="group-details__title" v-html="item.achievementTitle || ''" @click.stop="onAchievementDetails(item)"></div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      :active="descriptionModal"
      name="AchievementDescriptionModal"
      class="achievement__description"
      :class="{ 'for-mobile-app': isMobileApp }"
      @close="descriptionModal = false"
    >
      <div class="achievement__description-img">
        <img :src="descriptionImageClass" :alt="descriptionTitle" />
      </div>
      <div class="achievement__description-title" v-html="descriptionTitle"></div>
      <div class="achievement__description-subtitle" v-html="descriptionSubtitle"></div>
      <Button
        text="Назад к достижениям"
        variant="secondary"
        size="lg"
        wide
        mobile
        @click="descriptionModal = false"
      ></Button>
    </Modal>

    <Modal
      :active="indexOFPModal"
      :img="ofpImage"
      :title="ofpTitle"
      :subtitle="ofpSubtitle"
      name="IndexOFPModal"
      class="index-ofp"
      :class="{ 'for-mobile-app': isMobileApp }"
      @close="indexOFPModal = false"
    >
      <div class="ofp">
        <div class="ofp__value">
          <Slider
            v-if="indexOFPModal && ofpValues.length > 0"
            :initialSlide="initialSlide"
            :navigationOffset="navigationOffset"
            :noPagination="noPagination"
            class="ofp__slider"
          >
            <IndexOFP
              v-for="(item, i) in ofpValues"
              :key="i"
              :value="ofpValues[i].value"
              :dates="ofpValues[i].dates"
              :simple="true"
            />
          </Slider>
        </div>
        <div class="ofp__description" v-html="ofpDescription"></div>
        <Button
          class="ofp-more"
          text="Узнать больше"
          variant="secondary"
          tag="a"
          :target="!isMobileApp ? '_blank' : ''"
          :href="articleAboutIndexOFP"
          size="lg"
          mobile
        ></Button>
      </div>
    </Modal>

    <SetAvatarModal
      :active="showSetAvatar"
      :isMobileApp="isMobileApp"
      class="set-avatar"
      @close="showSetAvatar = false"
    ></SetAvatarModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import SetAvatarModal from '@/components/SetAvatarModal/SetAvatarModal.vue';
import IndexOFP from '@/components/IndexOFP/IndexOFP.vue';

import WHOImage from '@/assets/img/index-ofp/who.svg';
import { formatDateRange, getArticleByKey } from '@/assets/js/helpers';

dayjs.extend(weekday);

const DIR = '/img/achievements';

export default {
  name: 'ProfileExperience',
  props: {
    achievements: Array,
    sportIndexes: Object,
    isMobileApp: Boolean,
    steps: {
      type: Number,
      default: 0,
    },
  },
  components: {
    SetAvatarModal,
    IndexOFP,
  },
  data() {
    return {
      categories: [
        { id: 'General', name: 'Общие', collapsed: false },
        { id: 'Team', name: 'Командные', collapsed: true },
        { id: 'Walking', name: 'Ходьба', collapsed: true },
        { id: 'Running', name: 'Бег', collapsed: true },
        { id: 'Cycling', name: 'Велозаезд', collapsed: true },
        { id: 'Swimming', name: 'Плавание', collapsed: true },
        { id: 'Skiing', name: 'Лыжи', collapsed: true },
        { id: 'Market', name: 'Маркет', collapsed: true },
      ],
      imageMore: `${DIR}/system-empty.svg`,
      achievementsModal: false,
      descriptionModal: false,
      descriptionImageClass: '',
      descriptionTitle: '',
      descriptionSubtitle: '',
      showSetAvatar: false,
      indexOFPModal: false,
      ofpImage: WHOImage,
      ofpTitle: 'ОФП-индекс на основе рекомендаций ВОЗ',
      ofpSubtitle: 'Еженедельно достигая <b>150 ОФП</b>, вы можете продлить себе жизнь, улучшить качество сна и поднять настроение.',
      ofpDescription: 'Индекс ОФП повышается за каждую минуту активных физических нагрузок, например бега.',
      ofpValues: [],
    };
  },
  computed: {
    ...mapGetters('settings', ['theme', 'options', 'resources']),
    ...mapGetters('content', ['articles']),
    showIndexOFP() {
      return this.options ? this.options.showIndexOFP === true : false;
    },
    showAchievementTypes() {
      return this.options ? this.options.showAchievementTypes || [] : [];
    },
    articleAboutIndexOFP() {
      const link = getArticleByKey(this.articles, 'key_10');
      return link || '#';
    },
    navigationOffset() {
      return {
        default: '0', md: '72', lg: '72', xl: '84',
      };
    },
    noPagination() {
      return true;
    },
    initialSlide() {
      return this.ofpValues.length > 0 ? this.ofpValues.length - 1 : 0;
    },
    formattedSteps() {
      let stepsCount;
      if (this.steps < 100000) {
        stepsCount = this.steps.toString();
      } else if (this.steps >= 1000000) {
        stepsCount = `${(this.steps / 1000000).toFixed(1)}M`;
      } else {
        stepsCount = `${(this.steps / 1000).toFixed(1)}K`;
      }
      return stepsCount;
    },
    stepsWord() {
      let word;
      if (this.steps % 10 === 1 && this.steps % 100 !== 11) {
        word = 'шаг';
      } else if ((this.steps % 10 >= 2 && this.steps % 10 <= 4) && (this.steps % 100 < 12 || this.steps % 100 > 14)) {
        word = 'шага';
      } else {
        word = 'шагов';
      }
      return word;
    },
    activeAchievements() {
      if (this.achievements) {
        const activeAchievements = this.achievements.filter((item) => item.unlocked);
        const lockedAchievements = this.achievements.filter((item) => !item.unlocked);
        return [...activeAchievements, ...lockedAchievements].slice(0, 8);
      }
      return [];
    },
    countMore() {
      if (this.achievements) {
        return Math.max(0, this.achievements.length - this.activeAchievements.length);
      }
      return 0;
    },
  },
  methods: {
    onSelected(achievementType) {
      if (achievementType) {
        this.achievementsModal = true;
      }
    },
    onAchievementDetails(item) {
      if (item) {
        this.descriptionImageClass = this.getAchievementIconUrl(item.achievementKey);
        this.descriptionTitle = item.achievementTitle;
        this.descriptionSubtitle = item.achievementDescription;
        this.descriptionModal = true;
      }
    },
    onShowIndexOFP() {
      this.indexOFPModal = true;
    },

    onHashChange() {
      if (this.$route.name !== 'Dashboard') {
        window.removeEventListener('hashchange', this.onHashChange);
        return;
      }
      const hash = window.location.hash.split('=');
      if (hash[0] === '#!showAchievements') this.showAchievements(hash);
      else if (hash[0] === '#!showOFP') this.showOFP(hash);
      else {
        this.achievementsModal = false;
        this.indexOFPModal = false;
      }
    },
    getAchievementsOfCat(cat) {
      if (!this.achievements) return [];
      const categoryAchievements = this.achievements.filter((item) => item.achievementCategory === cat);
      const activeAchievements = categoryAchievements.filter((item) => item.unlocked);
      const lockedAchievements = categoryAchievements.filter((item) => !item.unlocked);
      return [...activeAchievements, ...lockedAchievements];
    },
    getActiveOfCatCount(cat) {
      if (!this.achievements) return 0;
      return this.achievements.filter((item) => item.achievementCategory === cat && item.unlocked).length;
    },
    getCountOfCat(cat) {
      if (!this.achievements) return 0;
      return this.achievements.filter((item) => item.achievementCategory === cat).length;
    },

    setCollapsed(id) {
      const index = this.categories.findIndex((i) => i.id === id);
      if (index < 0) return;
      this.categories[index].collapsed = !this.categories[index].collapsed;
    },
    getAchievementName(achievementKey) {
      return achievementKey;
    },
    getAchievementIconUrl(achievementKey) {
      if (this.$props.achievements) {
        const found = this.$props.achievements.find((item) => item.achievementKey === achievementKey);
        if (found && found.achievementIconUrl) {
          return found.achievementIconUrl;
        }
      }
      return '';
    },
    setOFPvalues() {
      const values = [];
      if (this.sportIndexes) {
        const items = [];
        if (this.sportIndexes.history) {
          items.push(...this.sportIndexes.history);
        }
        items.push({
          index: this.sportIndexes.index,
          startDate: this.sportIndexes.startDate,
          finishDate: this.sportIndexes.finishDate,
        });
        for (let i = 0; i < items.length; i += 1) {
          const dates = formatDateRange({ start: items[i].startDate, finish: items[i].finishDate, showYear: true });
          values.push({
            value: items[i].index,
            dates,
          });
        }
      }
      this.ofpValues = values;
    },
    formatDateRange(payload) {
      return formatDateRange(payload);
    },
    showAchievements(hash) {
      if (hash[1] === 'true') this.onSelected('Welcome');
      else {
        this.achievementsModal = false;
        this.clearHash();
      }
    },
    showOFP(hash) {
      if (hash[1] === 'true') this.onShowIndexOFP();
      else {
        this.indexOFPModal = false;
        this.clearHash();
      }
    },
    clearHash() {
      setTimeout(() => { window.location.hash = ''; }, 100);
    },
  },
  mounted() {
    this.setOFPvalues();
    window.addEventListener('hashchange', this.onHashChange);
    this.onHashChange();
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.onHashChange);
  },
};
</script>

<style lang="scss" scoped>
  @import "./AchievementsParticipant";
</style>
